import { useState, useCallback, useEffect } from 'react';
import axios from 'axios';
import { joinURLs } from '../../../utils/urlUtil';
import { getHeadersConfig } from '../../../utils/config';
import ErrorHandler from '../../../utils/ErrorHandler';
import { LookupApp, FilterState } from '../types';
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll';

export const useLookupSearch = () => {
  // Load search term from localStorage
  const getInitialSearchTerm = (): string => {
    try {
      // First try the new key
      let searchTerm = localStorage.getItem('lookupSearchTerm');
      
      // If not found, try migrating from old key
      if (!searchTerm) {
        searchTerm = localStorage.getItem('productsLookupSearchTerm');
        if (searchTerm) {
          // Migrate to new key and remove old key
          localStorage.setItem('lookupSearchTerm', searchTerm);
          localStorage.removeItem('productsLookupSearchTerm');
        }
      }
      
      return searchTerm || '';
    } catch (error) {
      console.error('Error loading search term from localStorage:', error);
      return '';
    }
  };

  const [searchTerm, setSearchTerm] = useState(getInitialSearchTerm());
  const [loading, setLoading] = useState(false);
  const [apps, setApps] = useState<LookupApp[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [displayLimit, setDisplayLimit] = useState(50);
  // Infinite scroll is now always enabled
  const infiniteScrollEnabled = true;
  const [hasMoreResults, setHasMoreResults] = useState(false);

  // Load filters from localStorage on component mount
  const getInitialFilters = (): FilterState => {
    try {
      // First try the new key
      let savedFilters = localStorage.getItem('lookupFilters');
      
      // If not found, try migrating from old key
      if (!savedFilters) {
        savedFilters = localStorage.getItem('productsLookupFilters');
        if (savedFilters) {
          // Migrate to new key and remove old key
          localStorage.setItem('lookupFilters', savedFilters);
          localStorage.removeItem('productsLookupFilters');
        }
      }
      
      if (savedFilters) {
        const parsed = JSON.parse(savedFilters);
        
        // Migrate selectedClinksStatus from array to single value
        if (Array.isArray(parsed.selectedClinksStatus)) {
          // If the old array had the 'ALL' option selected, convert to null (no filter)
          const nonAllOptions = parsed.selectedClinksStatus.filter((option: any) => option.id !== 'ALL');
          parsed.selectedClinksStatus = nonAllOptions.length > 0 ? nonAllOptions[0] : null;
        }
        
        // If the old single value was 'ALL', convert to null
        if (parsed.selectedClinksStatus && parsed.selectedClinksStatus.id === 'ALL') {
          parsed.selectedClinksStatus = null;
        }
        
        return parsed;
      }
    } catch (error) {
      console.error('Error loading filters from localStorage:', error);
    }
    
    // Return default filters if none saved
    return {
      selectedCampaignStats: [],
      selectedCampaignHealth: [],
      selectedActivityStatus: [],
      selectedClinksStatus: null,
      minDemandSources: null,
      maxDemandSources: null,
      selectedDemandSources: [],
      selectedCountries: [],
      selectedPlacements: []
    };
  };

  const [filters, setFilters] = useState<FilterState>(getInitialFilters());

  // Save filters to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem('lookupFilters', JSON.stringify(filters));
    } catch (error) {
      console.error('Error saving filters to localStorage:', error);
    }
  }, [filters]);

  // Save search term to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('lookupSearchTerm', searchTerm);
    } catch (error) {
      console.error('Error saving search term to localStorage:', error);
    }
  }, [searchTerm]);


  const performSearch = useCallback(async (term: string) => {
    setLoading(true);
    setError(null);

    const url = joinURLs('admin/campaigns/query', process.env.REACT_APP_JUPITER_API_BASE_URL);
    const params: any = {
      search: term,
      returnStats: true,
      returnCampaigns: true,
      returnClinks: true,
      useExpandedSearchQuery: true,
      formattedOptimizations: true
    };

    // Add includeNonMatchingCampaigns
    params.includeNonMatchingCampaigns = true;

    // Add Campaign Stats filter parameters - always send all parameters when any selection is made
    if (filters.selectedCampaignStats && filters.selectedCampaignStats.length > 0) {
      const statsSelectedIds = filters.selectedCampaignStats.map(stat => stat.id);
      params.includeActive = statsSelectedIds.includes('active');
      params.includeInactive = statsSelectedIds.includes('inactive');
      params.includeNeedsApproval = statsSelectedIds.includes('needs-approval');
      params.includeNoWhitelist = statsSelectedIds.includes('no-whitelist');
      params.includePayoutIssues = statsSelectedIds.includes('payout-issues');
      params.includeNotInFeed = statsSelectedIds.includes('removed-from-feed');
      params.includeRegionTargeted = statsSelectedIds.includes('region-targeted');
      params.includeNonRegionTargeted = statsSelectedIds.includes('non-region-targeted');
    }

    // Add Campaign Health filter parameters - always send all parameters when any selection is made
    if (filters.selectedCampaignHealth && filters.selectedCampaignHealth.length > 0) {
      const healthSelectedIds = filters.selectedCampaignHealth.map(health => health.id);
      params.includeHealthyQC = healthSelectedIds.includes('healthy-qc');
      params.includeUnhealthyQC = healthSelectedIds.includes('unhealthy-qc');
      params.includeBrokenStats = healthSelectedIds.includes('broken-stats');
      params.includeWorkingStats = healthSelectedIds.includes('healthy-stats');
    }

    // Add Recent Activity filter parameters
    if (filters.selectedActivityStatus.length > 0) {
      const selectedIds = filters.selectedActivityStatus.map(activity => activity.id);
      params.reqActivityClicks24h = selectedIds.includes('clicks-24h');
      params.reqActivityClicks7d = selectedIds.includes('clicks-7d');
      params.reqActivityInstalls24h = selectedIds.includes('conversions-24h');
      params.reqActivityInstalls7d = selectedIds.includes('conversions-7d');
      params.activityNearDailyCap = selectedIds.includes('near-cap');
    }

    // Add Clinks Status filter parameters
    if (filters.selectedClinksStatus) {
      params.clinkFilter = filters.selectedClinksStatus.id;
    }
    
    // Add Uniqueness filter parameters (min/max demand sources)
    if (filters.minDemandSources !== null && filters.minDemandSources > 0) {
      params.minDemandSources = filters.minDemandSources;
    }
    if (filters.maxDemandSources !== null && filters.maxDemandSources > 0) {
      params.maxDemandSources = filters.maxDemandSources;
    }

    // Add Demand Source filter parameters
    if (filters.selectedDemandSources.length > 0) {
      params.allowDemandSources = filters.selectedDemandSources.map(s => s.demandSourceId).join(',');
    }

    // Add Country filter parameters
    if (filters.selectedCountries && filters.selectedCountries.length > 0) {
      params.countries = filters.selectedCountries.map(c => c.id).join(',');
    }

    // Add Publisher Placement filter parameters
    if (filters.selectedPlacements && filters.selectedPlacements.length > 0) {
      params.placements = filters.selectedPlacements.map(p => p.publisherPlacementId).join(',');
    }

    try {
      const response = await axios.get(url, {...getHeadersConfig(), params});
      const appsData = response.data.apps || [];

      // Process app data
      const processedApps = appsData.map((app: any) => {
        if (app.campaigns) {
          // Always force set campaignMatch flags based on count, ignoring any existing flags
          if (app.counts?.appMatchedCampaigns !== undefined) {
            app.campaigns.forEach((campaign: any, index: number) => {
              campaign.campaignMatch = index < app.counts.appMatchedCampaigns;
            });
            app.matchedCampaigns = app.campaigns.slice(0, app.counts.appMatchedCampaigns);
          } else {
            app.campaigns.forEach((campaign: any) => {
              campaign.campaignMatch = false;
            });
            app.matchedCampaigns = [];
          }
        } else {
          app.matchedCampaigns = [];
        }

        // For clinks, check if there's a similar match property, otherwise use all clinks
        if (app.matchedClinks && Array.isArray(app.matchedClinks)) {
          // API provided matchedClinks directly - keep as is
        } else if (app.clinks) {
          // Try to identify matched clinks
          const clinksWithMatch = app.clinks.filter((c: any) => c.clinkMatch === true);
          
          if (clinksWithMatch.length > 0) {
            app.matchedClinks = clinksWithMatch;
          } else if (app.counts?.appMatchedClinks > 0) {
            // Set clinkMatch flags based on the count - first N clinks are matching
            app.clinks.forEach((clink: any, index: number) => {
              clink.clinkMatch = index < app.counts.appMatchedClinks;
            });
            app.matchedClinks = app.clinks.slice(0, app.counts.appMatchedClinks);
          } else {
            // No matching clinks
            app.clinks.forEach((clink: any) => {
              clink.clinkMatch = false;
            });
            app.matchedClinks = [];
          }
        } else {
          app.matchedClinks = [];
        }

        return app;
      });

      setApps(processedApps);
      setDisplayLimit(50); // Reset display limit on new search
    } catch (error: any) {
      if (!axios.isCancel(error)) {
        const isLoggedOut = ErrorHandler.handleAuthError(error);
        if (!isLoggedOut) {
          setError('Could not query campaigns');
        }
      }
    } finally {
      setLoading(false);
    }
  }, [filters]);

  const fetchDetailedAppData = async (app: LookupApp) => {
    const url = joinURLs('admin/campaigns/query', process.env.REACT_APP_JUPITER_API_BASE_URL);
    const params: any = {
      search: app.title, // Search specifically for this app
      returnStats: true,
      returnCampaigns: true,
      returnClinks: true,
      includeNonMatchingCampaigns: true,
      useExpandedSearchQuery: true,
      formattedOptimizations: true
    };

    // Add current filter parameters to maintain consistency with the main search
    if (filters.selectedCampaignStats && filters.selectedCampaignStats.length > 0) {
      const selectedIds = filters.selectedCampaignStats.map(stat => stat.id);
      params.includeActive = selectedIds.includes('active');
      params.includeInactive = selectedIds.includes('inactive');
      params.includeNeedsApproval = selectedIds.includes('needs-approval');
      params.includeNoWhitelist = selectedIds.includes('no-whitelist');
      params.includePayoutIssues = selectedIds.includes('payout-issues');
      params.includeNotInFeed = selectedIds.includes('removed-from-feed');
      params.includeRegionTargeted = selectedIds.includes('region-targeted');
      params.includeNonRegionTargeted = selectedIds.includes('non-region-targeted');
    }

    if (filters.selectedCampaignHealth && filters.selectedCampaignHealth.length > 0) {
      const selectedIds = filters.selectedCampaignHealth.map(health => health.id);
      params.includeHealthyQC = selectedIds.includes('healthy-qc');
      params.includeUnhealthyQC = selectedIds.includes('unhealthy-qc');
      params.includeBrokenStats = selectedIds.includes('broken-stats');
      params.includeWorkingStats = selectedIds.includes('healthy-stats');
    }

    if (filters.selectedActivityStatus.length > 0) {
      const selectedIds = filters.selectedActivityStatus.map(activity => activity.id);
      params.reqActivityClicks24h = selectedIds.includes('clicks-24h');
      params.reqActivityClicks7d = selectedIds.includes('clicks-7d');
      params.reqActivityInstalls24h = selectedIds.includes('conversions-24h');
      params.reqActivityInstalls7d = selectedIds.includes('conversions-7d');
      params.activityNearDailyCap = selectedIds.includes('near-cap');
    }

    if (filters.selectedClinksStatus) {
      params.clinkFilter = filters.selectedClinksStatus.id;
    }
    
    if (filters.minDemandSources !== null && filters.minDemandSources > 0) {
      params.minDemandSources = filters.minDemandSources;
    }
    if (filters.maxDemandSources !== null && filters.maxDemandSources > 0) {
      params.maxDemandSources = filters.maxDemandSources;
    }

    if (filters.selectedDemandSources.length > 0) {
      params.allowDemandSources = filters.selectedDemandSources.map(s => s.demandSourceId).join(',');
    }

    if (filters.selectedCountries && filters.selectedCountries.length > 0) {
      params.countries = filters.selectedCountries.map(c => c.id).join(',');
    }

    if (filters.selectedPlacements && filters.selectedPlacements.length > 0) {
      params.placements = filters.selectedPlacements.map(p => p.publisherPlacementId).join(',');
    }

    try {
      const response = await axios.get(url, {...getHeadersConfig(), params});
      const appsData = response.data.apps || [];
      
      // Find the matching app in the response
      const detailedApp = appsData.find((responseApp: any) => responseApp.appID === app.appID);
      
      if (detailedApp) {
        // Process the detailed app data - preserve original search counts, don't use detailed API counts
        if (detailedApp.campaigns) {
          // Use the original counts from the search, not the detailed API counts
          const originalMatchedCount = app.counts?.appMatchedCampaigns || 0;
          
          detailedApp.campaigns.forEach((campaign: any, index: number) => {
            campaign.campaignMatch = index < originalMatchedCount;
          });
          detailedApp.matchedCampaigns = detailedApp.campaigns.slice(0, originalMatchedCount);
          
          // Also preserve the original counts
          detailedApp.counts = { ...detailedApp.counts, ...app.counts };
        } else {
          detailedApp.matchedCampaigns = [];
        }

        // Process matched clinks similarly - use original counts
        if (detailedApp.clinks) {
          const originalMatchedClinksCount = app.counts?.appMatchedClinks || 0;
          
          detailedApp.clinks.forEach((clink: any, index: number) => {
            clink.clinkMatch = index < originalMatchedClinksCount;
          });
          detailedApp.matchedClinks = detailedApp.clinks.slice(0, originalMatchedClinksCount);
        } else {
          detailedApp.matchedClinks = [];
        }

        return detailedApp;
      }
    } catch (error: any) {
      if (!axios.isCancel(error)) {
        const isLoggedOut = ErrorHandler.handleAuthError(error);
        if (!isLoggedOut) {
          console.error('Failed to fetch detailed app data:', error);
        }
      }
    }
    
    return null;
  };

  const clearFilters = () => {
    setSearchTerm('');
    setDisplayLimit(50);
    setFilters({
      selectedCampaignStats: [],
      selectedCampaignHealth: [],
      selectedActivityStatus: [],
      selectedClinksStatus: null,
      minDemandSources: null,
      maxDemandSources: null,
      selectedDemandSources: [],
      selectedCountries: [],
      selectedPlacements: []
    });
  };

  const handleShowMore = useCallback(() => {
    setDisplayLimit(prev => prev + 50);
  }, []);

  // Infinite scroll callback
  const handleInfiniteScroll = useCallback(() => {
    if (hasMoreResults && !loading) {
      handleShowMore();
    }
  }, [hasMoreResults, loading, handleShowMore]);

  // Initialize infinite scroll
  const { isFetching, observerRef } = useInfiniteScroll(
    handleInfiniteScroll, 
    { 
      enabled: infiniteScrollEnabled && hasMoreResults,
      threshold: 300 
    }
  );

  // Update hasMoreResults when apps or displayLimit changes
  useEffect(() => {
    setHasMoreResults(apps.length > displayLimit);
  }, [apps.length, displayLimit]);

  return {
    // State
    searchTerm,
    loading,
    apps,
    error,
    filters,
    displayLimit,
    hasMoreResults,
    isFetching,
    
    // Refs
    observerRef,
    
    // Actions
    setSearchTerm,
    setApps,
    setError,
    setFilters,
    performSearch,
    fetchDetailedAppData,
    clearFilters
  };
};